import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Calendar, GripVertical, Star } from "lucide-react";
import { format, isPast } from "date-fns";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Task } from "@shared/schema";
import { assignedTaskCardClass, isAssignedToAssistant } from "@/components/tasks/assigned-task-highlight";

type KanbanColumn = {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  color: string;
  displayOrder: number;
  createdAt: Date | string;
  updatedAt: Date | string;
};

interface KanbanBoardProps {
  viewerId?: string;
  isAssistantViewer?: boolean;
  tasks: Task[];
  columns: KanbanColumn[];
  onTaskMove: (taskId: string, columnId: string | null) => void;
  onColumnEdit: (column: KanbanColumn) => void;
  onColumnDelete: (columnId: string) => void;
  onColumnReorder: (columnIds: string[]) => void;
  onCreateTask: (columnId: string | null) => void;
  onTaskClick: (task: Task) => void;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onTopThreeToggle: (taskId: string) => void;
  onTaskEdit: (task: Task) => void;
}

interface SortableColumnProps {
  viewerId?: string;
  isAssistantViewer?: boolean;
  column: KanbanColumn | { id: string; title: string; color: string };
  tasks: Task[];
  onEdit: () => void;
  onDelete: () => void;
  onCreateTask: () => void;
  onTaskClick: (task: Task) => void;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onTopThreeToggle: (taskId: string) => void;
  onTaskEdit: (task: Task) => void;
  isDefaultColumn?: boolean;
}

interface SortableTaskCardProps {
  highlightAssigned?: boolean;
  task: Task;
  onClick: () => void;
  onToggle: (completed: boolean) => void;
  onTopThreeToggle: (taskId: string) => void;
  onEdit: (task: Task) => void;
}

function SortableTaskCard({ task, onClick, onToggle, onTopThreeToggle, onEdit, highlightAssigned }: SortableTaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isOverdue = !task.completed && task.dueDate && isPast(new Date(task.dueDate));

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`cursor-pointer transition-shadow hover:shadow-md ${
        highlightAssigned ? assignedTaskCardClass : isOverdue ? "border-red-300 bg-red-50 dark:bg-red-950/20" : ""
      }`}
      data-testid={`task-card-${task.id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <Checkbox
            checked={task.completed}
            onCheckedChange={(checked) => {
              onToggle(checked as boolean);
            }}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5"
            data-testid={`checkbox-task-${task.id}`}
          />
          <div className="flex-1 min-w-0" onClick={onClick}>
            <div className="flex items-start justify-between gap-2">
              <h4
                className={`font-medium text-sm flex-1 ${
                  task.completed ? "line-through text-muted-foreground" : ""
                }`}
              >
                {task.title}
              </h4>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                  data-testid={`button-edit-task-${task.id}`}
                  title="Edit task"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTopThreeToggle(task.id);
                  }}
                  data-testid={`button-toggle-top-three-${task.id}`}
                  title={task.isTopThree ? "Remove from Top Three" : "Add to Top Three"}
                >
                  <Star 
                    className="h-3 w-3" 
                    fill={task.isTopThree ? "#f59e0b" : "none"}
                    style={{ color: task.isTopThree ? "#f59e0b" : "currentColor" }}
                  />
                </Button>
              </div>
            </div>
            {task.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {task.description}
              </p>
            )}
            {task.dueDate && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${
                isOverdue ? "text-red-600 dark:text-red-400" : "text-muted-foreground"
              }`}>
                <Calendar className="w-3 h-3" />
                <span>{format(new Date(task.dueDate), "MMM d, yyyy")}</span>
              </div>
            )}
          </div>
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <GripVertical className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SortableColumn({
  viewerId,
  isAssistantViewer,
  column,
  tasks,
  onEdit,
  onDelete,
  onCreateTask,
  onTaskClick,
  onTaskToggle,
  onTopThreeToggle,
  onTaskEdit,
  isDefaultColumn = false,
}: SortableColumnProps) {
  const {
    attributes,
    listeners,
    setNodeRef: setSortableNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id, disabled: isDefaultColumn });

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: `droppable-${column.id}`,
    data: { type: 'column', columnId: column.id }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const setNodeRef = (node: HTMLElement | null) => {
    setSortableNodeRef(node);
    setDroppableNodeRef(node);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex-shrink-0 w-[300px] flex flex-col max-h-full"
    >
      <Card className="flex flex-col h-full" style={{ borderLeftColor: column.color, borderLeftWidth: "4px" }}>
        <CardHeader className="p-4 pb-3" style={{ backgroundColor: `${column.color}15` }}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {!isDefaultColumn && (
                <div
                  {...attributes}
                  {...listeners}
                  className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground"
                >
                  <GripVertical className="w-4 h-4" />
                </div>
              )}
              <h3 className="font-semibold truncate">{column.title}</h3>
              <Badge variant="secondary" className="ml-auto">
                {tasks.length}
              </Badge>
            </div>
            {!isDefaultColumn && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onEdit}
                  data-testid={`button-edit-column-${column.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={onDelete}
                  data-testid={`button-delete-column-${column.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-3 flex-1 overflow-y-auto space-y-3">
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {isDefaultColumn ? "No tasks match your filters" : "Drop tasks here or click Add Task"}
            </div>
          ) : (
            <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              {tasks.map((task) => (
                <SortableTaskCard
                  key={task.id}
                  task={task}
                  highlightAssigned={isAssignedToAssistant(task, viewerId, !!isAssistantViewer)}
                  onClick={() => onTaskClick(task)}
                  onToggle={(completed) => onTaskToggle(task.id, completed)}
                  onTopThreeToggle={onTopThreeToggle}
                  onEdit={onTaskEdit}
                />
              ))}
            </SortableContext>
          )}
          <Button
            variant="outline"
            className="w-full"
            onClick={onCreateTask}
            data-testid={`button-add-task-${column.id}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function KanbanBoard({
  viewerId,
  isAssistantViewer,
  tasks,
  columns,
  onTaskMove,
  onColumnEdit,
  onColumnDelete,
  onColumnReorder,
  onCreateTask,
  onTaskClick,
  onTaskToggle,
  onTopThreeToggle,
  onTaskEdit,
}: KanbanBoardProps) {
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedColumns = [...columns].sort((a, b) => a.displayOrder - b.displayOrder);

  const allTasksColumn = {
    id: "all-tasks",
    title: "All Tasks",
    color: "#3b82f6",
  };

  const allColumns = [allTasksColumn, ...sortedColumns];

  const getTasksForColumn = (columnId: string) => {
    if (columnId === "all-tasks") {
      return tasks;
    }
    return tasks.filter((task) => task.columnId === columnId);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeColumn = sortedColumns.find(col => col.id === activeId);
    const overColumn = sortedColumns.find(col => col.id === overId);
    
    if (activeColumn && overColumn && activeId !== overId) {
      const oldIndex = sortedColumns.findIndex(col => col.id === activeId);
      const newIndex = sortedColumns.findIndex(col => col.id === overId);
      
      const reorderedColumns = [...sortedColumns];
      const [movedColumn] = reorderedColumns.splice(oldIndex, 1);
      reorderedColumns.splice(newIndex, 0, movedColumn);
      
      const columnIds = reorderedColumns.map(col => col.id);
      onColumnReorder(columnIds);
      return;
    }

    const taskId = activeId;
    let newColumnId: string | null = null;
    
    if (overId.startsWith('droppable-')) {
      const columnId = overId.replace('droppable-', '');
      newColumnId = columnId === "all-tasks" ? null : columnId;
    } else {
      const overCol = allColumns.find((col) => col.id === overId);
      if (overCol) {
        newColumnId = overCol.id === "all-tasks" ? null : overCol.id;
      } else {
        const overTask = tasks.find((t) => t.id === overId);
        if (overTask) {
          newColumnId = overTask.columnId;
        }
      }
    }

    const task = tasks.find((t) => t.id === taskId);
    if (task && task.columnId !== newColumnId) {
      onTaskMove(taskId, newColumnId);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 h-full md:flex-row flex-col">
        <SortableContext items={allColumns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
          {allColumns.map((column) => (
            <SortableColumn
              key={column.id}
              viewerId={viewerId}
              isAssistantViewer={isAssistantViewer}
              column={column}
              tasks={getTasksForColumn(column.id)}
              onEdit={() => {
                if (column.id !== "all-tasks") {
                  onColumnEdit(column as KanbanColumn);
                }
              }}
              onDelete={() => column.id !== "all-tasks" && onColumnDelete(column.id)}
              onCreateTask={() => onCreateTask(column.id === "all-tasks" ? null : column.id)}
              onTaskClick={onTaskClick}
              onTaskToggle={onTaskToggle}
              onTopThreeToggle={onTopThreeToggle}
              onTaskEdit={onTaskEdit}
              isDefaultColumn={column.id === "all-tasks"}
            />
          ))}
        </SortableContext>
      </div>

      <DragOverlay>
        {activeTask ? (
          <Card className={`w-[300px] shadow-lg opacity-90 ${
            isAssignedToAssistant(activeTask, viewerId, !!isAssistantViewer) ? assignedTaskCardClass : ""
          }`}>
            <CardContent className="p-3">
              <div className="flex items-start gap-2">
                <Checkbox checked={activeTask.completed} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h4
                    className={`font-medium text-sm ${
                      activeTask.completed ? "line-through text-muted-foreground" : ""
                    }`}
                  >
                    {activeTask.title}
                  </h4>
                  {activeTask.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {activeTask.description}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
